/**
 * קו SIP אחד לכל דפדפן.
 *
 * 🔑 29/09/2026 (קמפיין 7, נציג 813): שתי לשוניות GoCall רשמו את אותה שלוחה
 * כל היום. באסטריסק max_contacts=1 + remove_existing ⇒ כל רישום מחדש (כל כמה
 * דקות) העיף את השנייה, והשיחה נענתה אוטומטית (X-Auto-Answer) בלשונית שרשמה
 * אחרונה — לא בהכרח זו שהנציג מול העיניים. הנציג לא שמע את הלקוח, והלקוח שמע
 * אותו "למחצה".
 *
 * Web Locks: רק הלשונית שמחזיקה את המנעול רושמת את הקו. האחרות ממתינות בתור
 * ותופסות אותו לבד כשהמחזיקה נסגרת. פעולת חיוג מפורשת בלשונית ממתינה (סבב,
 * "התחבר מחדש") גונבת את המנעול — הקו עובר לאן שהנציג עובד.
 * בלי navigator.locks (דפדפן ישן / jsdom) — התנהגות קודמת, בלי נעילה.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LockCallback = (lock: unknown) => any;
interface LockManagerLike {
  request(name: string, options: { ifAvailable?: boolean; steal?: boolean; signal?: AbortSignal }, cb: LockCallback): Promise<unknown>;
}

function lockManager(): LockManagerLike | null {
  const nav = typeof navigator !== 'undefined' ? (navigator as unknown as { locks?: LockManagerLike }) : null;
  return nav?.locks ?? null;
}

let held: { name: string; release: () => void } | null = null;
let waiter: AbortController | null = null;

export function sipLockName(uri: string): string {
  return `gocall-sip:${uri}`;
}

export function holdsSipLock(name: string): boolean {
  return !lockManager() || held?.name === name;
}

export function cancelSipLockWait(): void {
  waiter?.abort();
  waiter = null;
}

function releaseHeld(): void {
  held?.release();
  held = null;
}

/**
 * מנסה לתפוס את הקו. steal=true לוקח אותו מלשונית אחרת (שתקבל onLost).
 * מחזיר false כשלשונית אחרת מחזיקה ולא ביקשנו לגנוב.
 */
export function acquireSipLock(name: string, opts: { steal?: boolean; onLost: () => void }): Promise<boolean> {
  const locks = lockManager();
  if (!locks || held?.name === name) return Promise.resolve(true);
  releaseHeld();
  cancelSipLockWait();

  return new Promise((resolve) => {
    locks
      .request(name, opts.steal ? { steal: true } : { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(false);
          return undefined;
        }
        return new Promise<void>((release) => {
          held = { name, release };
          resolve(true);
        });
      })
      .catch(() => {
        // AbortError — לשונית אחרת גנבה את המנעול.
        if (held?.name === name) {
          held = null;
          opts.onLost();
        }
        resolve(false);
      });
  });
}

/** ממתין בתור עד שהמחזיקה משחררת (נסגרת), ואז תופס ומודיע. */
export function waitForSipLock(name: string, opts: { onGranted: () => void; onLost: () => void }): void {
  const locks = lockManager();
  if (!locks) return;
  cancelSipLockWait();
  const ctrl = new AbortController();
  waiter = ctrl;

  locks
    .request(name, { signal: ctrl.signal }, () => {
      if (waiter === ctrl) waiter = null;
      return new Promise<void>((release) => {
        held = { name, release };
        opts.onGranted();
      });
    })
    .catch(() => {
      if (ctrl.signal.aborted) return; // בוטל מכאן — לא אירוע
      if (held?.name === name) {
        held = null;
        opts.onLost();
      }
    });
}

/** לטסטים בלבד. */
export function __resetSipTabLockForTests(): void {
  cancelSipLockWait();
  releaseHeld();
}
